import { useCallback, useEffect } from 'react';

type UseListboxKeyboardNavigationParams = {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  triggerRef: React.RefObject<HTMLButtonElement | null>;
  listRef: React.RefObject<HTMLElement | null>;
  /**
   * CSS selector that resolves to the currently-selected option inside listRef.
   * Example: `[data-selection-option="true"][data-selected="true"]`
   */
  selectedOptionSelector: string;
};

type CreateOptionKeyDownHandler = (
  onSelect: () => void
) => (e: React.KeyboardEvent<HTMLButtonElement>) => void;

function focusFirstOption(listEl: HTMLElement | null) {
  const first = listEl?.querySelector(
    `[data-selection-option="true"]`
  ) as HTMLButtonElement | null;
  first?.focus();
}

function focusLastOption(listEl: HTMLElement | null) {
  const options = listEl?.querySelectorAll(`[data-selection-option="true"]`) as
    | NodeListOf<HTMLButtonElement>
    | undefined;
  if (!options?.length) return;
  options[options.length - 1]?.focus();
}

export default function useListboxKeyboardNavigation({
  open,
  setOpen,
  triggerRef,
  listRef,
  selectedOptionSelector,
}: UseListboxKeyboardNavigationParams): {
  onTriggerKeyDown: (e: React.KeyboardEvent<HTMLButtonElement>) => void;
  createOptionKeyDownHandler: CreateOptionKeyDownHandler;
} {
  const focusSelectedOption = useCallback(() => {
    const selectedEl = listRef.current?.querySelector(
      selectedOptionSelector
    ) as HTMLButtonElement | null;
    selectedEl?.focus();
  }, [listRef, selectedOptionSelector]);

  const onTriggerKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setOpen((v) => !v);
        return;
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setOpen(true);
        window.setTimeout(() => {
          focusSelectedOption();
        }, 0);
        return;
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setOpen(true);
        window.setTimeout(() => {
          focusLastOption(listRef.current);
        }, 0);
      }
    },
    [focusSelectedOption, listRef, setOpen]
  );

  const createOptionKeyDownHandler = useCallback<CreateOptionKeyDownHandler>(
    (onSelect) => (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onSelect();
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
        triggerRef.current?.focus();
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const next =
          (e.currentTarget.nextElementSibling as HTMLButtonElement | null) ??
          null;
        if (next) next.focus();
        else focusFirstOption(listRef.current);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        const prev =
          (e.currentTarget
            .previousElementSibling as HTMLButtonElement | null) ?? null;
        if (prev) prev.focus();
        else focusLastOption(listRef.current);
        return;
      }
      if (e.key === 'Home') {
        e.preventDefault();
        focusFirstOption(listRef.current);
        return;
      }
      if (e.key === 'End') {
        e.preventDefault();
        focusLastOption(listRef.current);
      }
    },
    [listRef, setOpen, triggerRef]
  );

  useEffect(() => {
    if (!open) return undefined;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open, setOpen, triggerRef]);

  return { onTriggerKeyDown, createOptionKeyDownHandler };
}
